
import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Ticket,
  Car,
  Users,
  Settings,
  LogOut,
  Bell,
  Menu,
  Inbox,
  BarChart3,
  Contact,
  CreditCard,
  HelpCircle,
  Moon,
  Sun,
  Check,
  X,
  AlertTriangle,
  Info,
  Crown,
  Receipt,
  BriefcaseBusiness,
  PanelLeftClose,
  PanelLeftOpen
} from 'lucide-react';
import { supabase } from '../services/supabaseClient';
import { useBranding } from '../context/BrandingContext';
import { useAuth } from '../App';
import { isAdminRole, roleLabel as getRoleLabel } from '../utils/roles';
import { useTheme } from '../context/ThemeContext';
import { useSubscription } from '../context/SubscriptionContext';
import { AIAssistant } from './AIAssistant';
import FeedbackModal from './FeedbackModal';
import { ApiService } from '../services/api';
import TicketSearch from './TicketSearch';
import BrandLogo from './BrandLogo';
import { AppModule, resolveModuleAccess } from '../services/moduleAccess';

interface SidebarItemProps {
  to: string;
  icon: React.ReactNode;
  label: string;
  isCollapsed?: boolean;
}

const SidebarItem: React.FC<SidebarItemProps> = ({ to, icon, label, isCollapsed = false }) => {
  const location = useLocation();
  const isActive = to === '/' 
    ? location.pathname === '/' 
    : location.pathname.startsWith(to);

  return (
    <Link
      to={to}
      title={isCollapsed ? label : undefined}
      className={`flex items-center gap-3 py-3 text-sm font-medium transition-all duration-200 rounded-xl mb-1 group ${
        isCollapsed ? 'px-4 lg:justify-center lg:px-0' : 'px-4'
      } ${
        isActive
          ? 'bg-primary-600/10 text-primary-400 shadow-[0_0_15px_rgb(var(--color-primary-500)_/_0.18)] border border-primary-500/20'
          : 'text-slate-400 hover:text-white hover:bg-white/5 border border-transparent'
      }`}
    >
      <div className={`${isActive ? 'text-primary-400' : 'text-slate-400 group-hover:text-white'} transition-colors`}>
        {icon}
      </div>
      <span className={isCollapsed ? 'truncate lg:sr-only' : 'truncate'}>{label}</span>
    </Link>
  );
};

const Layout: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { branding, organization } = useBranding();
  const { role, session, profile } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { tier } = useSubscription();
  
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = React.useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem('fleetmode.sidebarCollapsed') === 'true';
  });
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [isInternalStaff, setIsInternalStaff] = useState(false);
  const showTicketSearch = location.pathname.startsWith('/tickets');
  
  // Notification State
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [notifications, setNotifications] = useState<any[]>([]);
  const notifRef = useRef<HTMLDivElement>(null);
  const moduleAccess = React.useMemo(
    () => resolveModuleAccess({ organization, role }),
    [organization, role]
  );

  // Close notifications on click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (notifRef.current && !notifRef.current.contains(event.target as Node)) {
        setIsNotificationsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    window.localStorage.setItem('fleetmode.sidebarCollapsed', String(isSidebarCollapsed));
  }, [isSidebarCollapsed]);

  useEffect(() => {
    if (!session) {
      setIsInternalStaff(false);
      return;
    }

    let cancelled = false;
    const loadInternalAccess = async () => {
      const allowed = await ApiService.isCurrentUserInternalStaff();
      if (!cancelled) setIsInternalStaff(allowed);
    };

    void loadInternalAccess();
    return () => {
      cancelled = true;
    };
  }, [session]);

  // Keyboard shortcut: Shift + / to open Help
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName?.toLowerCase();
      const isTyping =
        tag === 'input' ||
        tag === 'textarea' ||
        tag === 'select' ||
        (target as any)?.isContentEditable;

      if (isTyping) return;

      if (e.key === '?' || (e.key === '/' && e.shiftKey)) {
        e.preventDefault();
        setIsFeedbackOpen(true);
      }
    };

    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // Load Notifications from Tasks
  useEffect(() => {
    if (!session) return;

    let cancelled = false;

    const systemNotif = {
      id: 'sys-1',
      title: 'System Update',
      message: 'PCNGuard v2.1 is live. New AI models enabled.',
      type: 'info',
      time: '2h ago',
      link: null
    };

    const loadNotifications = async () => {
      // Don't try to load notifications if there's no session
      if (!session) {
        setNotifications([systemNotif]);
        return;
      }

      try {
        const tasks = await ApiService.getTasks();
        if (cancelled) return;

        // Filter for open high/medium priority tasks
        const activeTasks = tasks
          .filter((t) => {
            const severity = String((t as any).severity || '').toLowerCase();
            const priority = String(t.priority || '').toLowerCase();
            const rank = severity || priority;
            return t.status === 'open' && ['critical', 'high', 'medium'].includes(rank);
          })
          .map(t => ({
            id: t.id,
            title: t.title,
            message: t.description,
            type: String((t as any).severity || t.priority || '').toLowerCase() === 'critical' || t.priority === 'High'
              ? 'critical'
              : 'alert',
            time: 'Active Now',
            link: t.related_id ? `/tickets/${t.related_id}` : '/tasks'
          }));

        setNotifications([systemNotif, ...activeTasks]);
      } catch (err) {
        if (cancelled) return;
        console.warn('Failed to load notifications:', err);
        setNotifications([systemNotif]);
      }
    };

    void loadNotifications();
    return () => {
      cancelled = true;
    };
  }, [session]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  const removeNotification = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const navigationItems: Array<{
    module: AppModule;
    to: string;
    icon: React.ReactNode;
    label: string;
    section: 'operations' | 'fleet' | 'admin' | 'system';
  }> = [
    { module: 'dashboard', to: '/', icon: <LayoutDashboard size={18} />, label: 'Dashboard', section: 'operations' },
    { module: 'tasks', to: '/tasks', icon: <Inbox size={18} />, label: 'Inbox', section: 'operations' },
    { module: 'tickets', to: '/tickets', icon: <Ticket size={18} />, label: 'Tickets', section: 'operations' },
    { module: 'vehicles', to: '/vehicles', icon: <Car size={18} />, label: 'Vehicles', section: 'fleet' },
    { module: 'drivers', to: '/drivers', icon: <Contact size={18} />, label: 'Drivers', section: 'fleet' },
    { module: 'hires', to: '/hires', icon: <Users size={18} />, label: 'Hires', section: 'fleet' },
    { module: 'driverInvoices', to: '/driver-invoices', icon: <Receipt size={18} />, label: 'Driver Invoices', section: 'fleet' },
    { module: 'reports', to: '/reports', icon: <BarChart3 size={18} />, label: 'Reports', section: 'admin' },
    { module: 'billing', to: '/billing', icon: <CreditCard size={18} />, label: 'Billing', section: 'admin' },
  ];

  const visibleNavigationItems = navigationItems.filter((item) => moduleAccess.canAccessModule(item.module));
  const operationsItems = visibleNavigationItems.filter((item) => item.section === 'operations');
  const fleetItems = visibleNavigationItems.filter((item) => item.section === 'fleet');
  const adminItems = visibleNavigationItems.filter((item) => item.section === 'admin');

  return (
    <div className="flex h-screen bg-slate-50 dark:bg-slate-950 overflow-hidden transition-colors duration-300">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-slate-950 border-r border-white/5 shadow-2xl transform transition-[transform,width] duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 ${
          isSidebarCollapsed ? 'lg:w-20' : 'lg:w-64'
        } ${
          isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full relative">
          {/* Subtle Gradient in Sidebar */}
          <div className="absolute top-0 left-0 w-full h-64 bg-gradient-to-b from-primary-900/20 to-transparent pointer-events-none" />

          {/* Logo */}
          <div className={`flex items-center h-16 border-b border-white/5 relative z-10 ${isSidebarCollapsed ? 'gap-2.5 px-5 lg:justify-center lg:px-3' : 'gap-2.5 px-5'}`}>
            <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center shadow-sm p-1.5 overflow-hidden ring-1 ring-slate-200/50">
              <BrandLogo
                loading="eager"
                decoding="sync"
                className="w-full h-full object-contain"
                fallbackIconClassName="w-full h-full rounded-md bg-white text-slate-900 text-[11px] font-semibold tracking-wide flex items-center justify-center"
              />
            </div>
            <span className={`text-base font-semibold tracking-tight truncate text-white ${isSidebarCollapsed ? 'lg:sr-only' : ''}`}>
              {branding.companyName}
            </span>
          </div>

          {/* Nav Items */}
          <nav className={`flex-1 py-6 space-y-1 overflow-y-auto relative z-10 scrollbar-thin scrollbar-thumb-slate-800 ${isSidebarCollapsed ? 'px-4 lg:px-3' : 'px-4'}`}>
            {operationsItems.length > 0 && (
              <>
                <div className={`text-[10px] font-bold text-slate-500 uppercase tracking-widest px-4 mb-2 mt-2 ${isSidebarCollapsed ? 'lg:hidden' : ''}`}>Operations</div>
                {operationsItems.map((item) => (
                  <SidebarItem key={item.to} to={item.to} icon={item.icon} label={item.label} isCollapsed={isSidebarCollapsed} />
                ))}
              </>
            )}

            {fleetItems.length > 0 && (
              <>
                <div className={`text-[10px] font-bold text-slate-500 uppercase tracking-widest px-4 mb-2 mt-6 ${isSidebarCollapsed ? 'lg:hidden' : ''}`}>Fleet CRM</div>
                {fleetItems.map((item) => (
                  <SidebarItem key={item.to} to={item.to} icon={item.icon} label={item.label} isCollapsed={isSidebarCollapsed} />
                ))}
              </>
            )}

            {adminItems.length > 0 && (
              <>
                <div className={`text-[10px] font-bold text-slate-500 uppercase tracking-widest px-4 mb-2 mt-6 ${isSidebarCollapsed ? 'lg:hidden' : ''}`}>Admin</div>
                {adminItems.map((item) => (
                  <SidebarItem key={item.to} to={item.to} icon={item.icon} label={item.label} isCollapsed={isSidebarCollapsed} />
                ))}
              </>
            )}

            {isInternalStaff && (
              <>
                <div className={`text-[10px] font-bold text-slate-500 uppercase tracking-widest px-4 mb-2 mt-6 ${isSidebarCollapsed ? 'lg:hidden' : ''}`}>Internal</div>
                <SidebarItem to="/internal/leads" icon={<BriefcaseBusiness size={18} />} label="Lead Desk" isCollapsed={isSidebarCollapsed} />
              </>
            )}

          </nav>

          {/* Footer Actions */}
          <div className="p-4 border-t border-white/5 relative z-10 bg-slate-950/50 backdrop-blur-sm">
            <button
              type="button"
              onClick={() => setIsSidebarCollapsed((value) => !value)}
              className={`hidden lg:flex items-center ${isSidebarCollapsed ? 'justify-center' : 'justify-between'} w-full gap-2 px-3 py-2 mb-3 text-xs font-medium text-slate-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors border border-transparent hover:border-white/5`}
              title={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              aria-label={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {isSidebarCollapsed ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}
              {!isSidebarCollapsed && <span>Collapse</span>}
            </button>

            {/* Feedback & Theme */}
            <div className={`${isSidebarCollapsed ? 'grid grid-cols-2 lg:flex lg:flex-col' : 'grid grid-cols-2'} gap-2 mb-4`}>
              <button
                onClick={() => setIsFeedbackOpen(true)}
                className="flex items-center justify-center gap-2 px-3 py-2 text-xs font-medium text-slate-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors border border-transparent hover:border-white/5"
                title="Help"
              >
                <HelpCircle size={16} /> <span className={isSidebarCollapsed ? 'lg:sr-only' : ''}>Help</span>
              </button>
              <button
                onClick={toggleTheme}
                className="flex items-center justify-center gap-2 px-3 py-2 text-xs font-medium text-slate-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors border border-transparent hover:border-white/5"
                title={theme === 'dark' ? 'Light' : 'Dark'}
              >
                {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
                <span className={isSidebarCollapsed ? 'lg:sr-only' : ''}>{theme === 'dark' ? 'Light' : 'Dark'}</span>
              </button>
            </div>

            {(() => {
              const displayEmail = profile.email ?? session?.user?.email ?? null;
              const displayName =
                (profile.fullName && profile.fullName.trim()) ||
                (displayEmail ? displayEmail.split('@')[0] : 'Account');
              const roleLabel = getRoleLabel(role);
              const initials = (() => {
                const base = String(displayName || '').trim();
                if (!base) return 'U';
                const parts = base.split(/\s+/).filter(Boolean);
                const letters = (parts.length >= 2 ? parts[0][0] + parts[1][0] : parts[0].slice(0, 2)).toUpperCase();
                return letters || 'U';
              })();

              return (
                <div
                  className={`mb-4 rounded-xl border border-white/10 bg-gradient-to-b from-white/5 to-white/0 ${isSidebarCollapsed ? 'px-3 py-3 lg:px-2 lg:py-2' : 'px-3 py-3'}`}
                  title={[displayName, roleLabel, displayEmail].filter(Boolean).join(' • ')}
                >
                  <div className={`flex items-center gap-3 ${isSidebarCollapsed ? 'lg:justify-center' : ''}`}>
                    <div
                      className={`w-10 h-10 rounded-xl flex items-center justify-center text-white border border-white/10 ${
                        isAdminRole(role)
                          ? 'bg-gradient-to-br from-primary-500 to-primary-700 shadow-lg shadow-primary-900/40'
                          : 'bg-gradient-to-br from-slate-600 to-slate-800'
                      }`}
                      aria-hidden="true"
                    >
                      <span className="text-xs font-extrabold tracking-wide">{initials}</span>
                    </div>
                    <div className={`min-w-0 flex-1 ${isSidebarCollapsed ? 'lg:sr-only' : ''}`}>
                      <div className="flex items-center justify-between gap-2 min-w-0">
                        <p className="text-sm font-semibold text-white truncate">{displayName}</p>
                        <span
                          className={`shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${
                            isAdminRole(role)
                              ? 'bg-purple-500/10 text-purple-200 border-purple-500/20'
                              : 'bg-blue-500/10 text-blue-200 border-blue-500/20'
                          }`}
                        >
                          {roleLabel}
                        </span>
                      </div>
                      <p className="text-xs text-slate-400 truncate">{displayEmail ?? '—'}</p>
                    </div>
                  </div>
                </div>
              );
            })()}
            
            <button
              onClick={handleLogout}
              className="flex items-center justify-center w-full gap-2 px-4 py-2 text-sm font-medium text-slate-400 hover:text-red-400 transition-colors rounded-lg hover:bg-red-500/10"
              title="Sign Out"
            >
              <LogOut size={16} />
              <span className={isSidebarCollapsed ? 'lg:sr-only' : ''}>Sign Out</span>
            </button>

            <div className={`mt-4 pt-3 border-t border-white/5 flex items-center justify-center gap-2 text-[10px] text-slate-500 uppercase tracking-widest ${isSidebarCollapsed ? 'lg:hidden' : ''}`}>
              <div className="w-5 h-5 rounded-md bg-white p-0.5 ring-1 ring-white/15">
                <BrandLogo
                  src="/logo.svg"
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-contain"
                  fallbackIconClassName="w-full h-full rounded-[4px] bg-white text-slate-700 text-[8px] font-semibold tracking-wide flex items-center justify-center"
                />
              </div>
              <span>Powered by PCNGuard Fleet</span>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 relative">
        <header className="h-16 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md border-b border-slate-200 dark:border-slate-800 flex items-center justify-between px-4 lg:px-8 z-30 sticky top-0 transition-colors duration-300">
          <button 
            className="p-2 text-slate-500 dark:text-slate-400 rounded-md lg:hidden hover:bg-slate-100 dark:hover:bg-slate-800"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            <Menu size={24} />
          </button>
          
          <div className="flex items-center justify-end w-full gap-4">
            
            {/* Plan Badge - Visible on larger screens */}
            {tier !== 'starter' && (
              <div className={`hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full border shadow-sm ${
                tier === 'managed' 
                  ? 'bg-amber-50 border-amber-200 text-amber-700 dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-400' 
                  : 'bg-primary-50 border-primary-200 text-primary-700 dark:bg-primary-900/20 dark:border-primary-800 dark:text-primary-400'
              }`}>
                 <Crown size={14} className={tier === 'managed' ? 'fill-current' : ''} />
                 <span className="text-xs font-bold uppercase tracking-wide">
                   {tier === 'managed' ? 'Concierge' : 'Pro Plan'}
                 </span>
              </div>
            )}

            {showTicketSearch && <TicketSearch />}

            {/* Notification Command Center */}
            <div className="relative" ref={notifRef}>
              <button 
                onClick={() => setIsNotificationsOpen(!isNotificationsOpen)}
                className={`relative p-2 rounded-full transition-colors ${
                  isNotificationsOpen 
                    ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400' 
                    : 'text-slate-400 dark:text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-600 dark:hover:text-slate-300'
                }`}
              >
                <Bell size={20} />
                {notifications.length > 0 && (
                  <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full border-2 border-white dark:border-slate-900 animate-pulse"></span>
                )}
              </button>

              {/* Notification Dropdown */}
              {isNotificationsOpen && (
                <div className="absolute right-0 mt-2 w-80 md:w-96 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl shadow-2xl overflow-hidden z-50 animate-in fade-in slide-in-from-top-2">
                  <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50 dark:bg-slate-950/50">
                    <h3 className="font-bold text-sm text-slate-800 dark:text-white">Command Center</h3>
                    {notifications.length > 0 && (
                      <button 
                        onClick={() => setNotifications([])}
                        className="text-xs text-primary-600 dark:text-primary-400 hover:underline font-medium"
                      >
                        Clear All
                      </button>
                    )}
                  </div>
                  
                  <div className="max-h-[400px] overflow-y-auto">
                    {notifications.length > 0 ? (
                      <div className="divide-y divide-slate-100 dark:divide-slate-800">
                        {notifications.map((n) => (
                          <div 
                            key={n.id} 
                            onClick={() => { 
                              if (n.link) {
                                navigate(n.link);
                                setIsNotificationsOpen(false);
                              }
                            }}
                            className={`p-4 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors cursor-pointer group relative ${
                              n.type === 'critical' ? 'bg-red-50/50 dark:bg-red-900/10' : ''
                            }`}
                          >
                            <button 
                              onClick={(e) => removeNotification(e, n.id)}
                              className="absolute top-2 right-2 p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X size={14} />
                            </button>
                            <div className="flex gap-3">
                              <div className={`mt-0.5 shrink-0 ${
                                n.type === 'critical' ? 'text-red-500' : 
                                n.type === 'alert' ? 'text-amber-500' : 
                                'text-blue-500'
                              }`}>
                                {n.type === 'critical' || n.type === 'alert' ? <AlertTriangle size={16} /> : <Info size={16} />}
                              </div>
                              <div>
                                <h4 className={`text-sm font-bold ${
                                  n.type === 'critical' ? 'text-red-700 dark:text-red-400' : 'text-slate-800 dark:text-slate-200'
                                }`}>
                                  {n.title}
                                </h4>
                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 line-clamp-2">{n.message}</p>
                                <span className="text-[10px] text-slate-400 mt-1.5 block font-medium">{n.time}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="py-12 flex flex-col items-center justify-center text-center px-6">
                        <div className="bg-slate-100 dark:bg-slate-800 p-3 rounded-full mb-3">
                          <Check size={24} className="text-slate-400" />
                        </div>
                        <p className="text-sm font-medium text-slate-900 dark:text-white">All caught up!</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">No new alerts or tasks.</p>
                      </div>
                    )}
                  </div>
                  
                  <div className="p-3 bg-slate-50 dark:bg-slate-950/50 border-t border-slate-100 dark:border-slate-800 text-center">
                    <Link 
                      to="/tasks" 
                      onClick={() => setIsNotificationsOpen(false)}
                      className="text-xs font-bold text-slate-600 dark:text-slate-400 hover:text-primary-600 dark:hover:text-primary-400 uppercase tracking-wide"
                    >
                      View All Activity
                    </Link>
                  </div>
                </div>
              )}
            </div>

            <Link
              to="/settings"
              className="p-2 rounded-full text-slate-400 dark:text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
              aria-label="Settings"
              title="Settings"
            >
              <Settings size={20} />
            </Link>
          </div>
        </header>

        <main className="flex-1 overflow-auto p-4 lg:p-8 bg-slate-50 dark:bg-slate-950 transition-colors duration-300 scroll-smooth">
          <div className="mx-auto w-full max-w-[1600px]">
            {children}
          </div>
        </main>

        {/* FleetMind AI Assistant */}
        <AIAssistant />
        
        {/* Global Feedback Modal */}
        <FeedbackModal isOpen={isFeedbackOpen} onClose={() => setIsFeedbackOpen(false)} />
      </div>

      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 z-40 bg-slate-950/80 backdrop-blur-sm lg:hidden animate-in fade-in"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}
    </div>
  );
};

export default Layout;
